import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchProductBySlug, fetchProducts } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { Heart, ShoppingBag, ArrowLeft, Truck, Recycle, Wrench } from "lucide-react";
import { toast } from "sonner";
import { ProductCard } from "../components/ProductCard";
import { SizeGuideDrawer } from "../components/SizeGuideDrawer";
import { RecentlyViewed } from "../components/RecentlyViewed";
import { Reviews } from "../components/Reviews";
import { useRecentlyViewed } from "../hooks/useRecentlyViewed";
import { useProductCopy } from "../hooks/useProductCopy";
import { JsonLd, productSchema } from "../components/JsonLd";
import { fetchReviews } from "../lib/api";

export default function ProductDetail() {
    const { t } = useTranslation();
    const { slug } = useParams();
    const [product, setProduct] = useState(null);
    const [size, setSize] = useState(null);
    const [color, setColor] = useState(null);
    const [activeImage, setActiveImage] = useState(0);
    const [related, setRelated] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
    const [seoReviews, setSeoReviews] = useState([]);
    const { addItem } = useCart();
    const { has, toggle } = useWishlist();
    const { items: recent, track } = useRecentlyViewed();
    const localized = useProductCopy(product);

    useEffect(() => {
        setLoading(true);
        setSize(null);
        setColor(null);
        setActiveImage(0);
        setSeoReviews([]);
        window.scrollTo({ top: 0, behavior: "instant" });
        fetchProductBySlug(slug)
            .then((p) => {
                setProduct(p);
                if (Array.isArray(p?.colors) && p.colors.length > 0) {
                    setColor(p.colors[0].name);
                }
                track(p);
                fetchReviews(slug).then(setSeoReviews).catch(() => {});
                return fetchProducts(p.category);
            })
            .then((all) => setRelated((all || []).filter((x) => x.slug !== slug).slice(0, 4)))
            .catch(() => setProduct(null))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    if (loading) {
        return (
            <div className="pt-32 container-rail">
                <div className="animate-pulse grid lg:grid-cols-2 gap-10">
                    <div className="aspect-[3/4] bg-[#141414]" />
                    <div className="space-y-4">
                        <div className="h-12 bg-[#141414] w-2/3" />
                        <div className="h-4 bg-[#141414] w-1/3" />
                    </div>
                </div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="pt-32 container-rail text-center py-20">
                <p className="text-overline text-[#A3A3A3]">{t("product.piece_not_found")}</p>
                <Link to="/shop" className="btn-ghost mt-6 inline-flex">{t("product.back_to_shop")}</Link>
            </div>
        );
    }

    const onAdd = () => {
        if (!size) {
            toast.error(t("product.select_size_first"));
            return;
        }
        const stock = product.stock?.[size] ?? null;
        if (stock === 0) {
            toast.error(t("product.size_sold_out_toast"));
            return;
        }
        addItem(product, size, color);
        toast.success(t("product.added_toast", { name: localized.name }));
    };

    const onColorPick = (c) => {
        setColor(c.name);
        // Swap the main image to the colour's hero shot when one matches
        const idx = product.images.findIndex((u) => u === c.image);
        if (idx >= 0) setActiveImage(idx);
    };

    const liked = has(product.id);
    const selectedStock = size ? product.stock?.[size] : null;

    return (
        <div data-testid="product-detail-page" className="pt-24">
            <JsonLd data={productSchema({ product, reviews: seoReviews })} />
            <div className="container-rail py-6">
                <Link to="/shop" className="text-overline text-[#A3A3A3] hover:text-[#F4F4F0] inline-flex items-center gap-2">
                    <ArrowLeft className="h-3 w-3" /> {t("product.back")}
                </Link>
            </div>

            <section className="container-rail grid lg:grid-cols-12 gap-10 pb-20">
                {/* Image column */}
                <div className="lg:col-span-7">
                    <div className="grid grid-cols-1 gap-3">
                        <div className="relative aspect-[4/5] overflow-hidden bg-[#141414] border border-white/[0.06]">
                            <img
                                src={product.images[activeImage]}
                                alt={`${product.name} — view ${activeImage + 1}`}
                                className="h-full w-full object-cover transition-opacity duration-500"
                                data-testid="product-main-image"
                                key={activeImage}
                            />
                            {product.is_new && (
                                <span className="absolute left-4 top-4 bg-[#EDEDE6] px-2.5 py-1 text-[10px] tracking-[0.22em] uppercase text-[#0C0C0C] font-display">
                                    {t("product.new_tag")}
                                </span>
                            )}
                        </div>
                        {product.images.length > 1 && (
                            <div className="grid grid-cols-4 gap-3" data-testid="product-thumbnails">
                                {product.images.map((img, i) => (
                                    <button
                                        key={img}
                                        data-testid={`product-thumb-${i}`}
                                        onClick={() => setActiveImage(i)}
                                        className={`relative aspect-[4/5] overflow-hidden bg-[#141414] border transition-colors ${
                                            activeImage === i ? "border-white" : "border-white/[0.06] hover:border-white/30"
                                        }`}
                                        aria-label={`Show image ${i + 1}`}
                                    >
                                        <img src={img} alt="" className="h-full w-full object-cover" />
                                        {activeImage === i && (
                                            <div className="absolute inset-0 bg-black/30" />
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Info column */}
                <div className="lg:col-span-5 lg:sticky lg:top-24 lg:self-start space-y-7">
                    <div>
                        <div className="text-overline text-[#A3A3A3]">{product.category} · {product.season}</div>
                        <h1 className="text-display text-5xl md:text-6xl leading-[0.95] mt-3" data-testid="product-name">
                            {localized.name}
                        </h1>
                        <div className="text-brutal text-xl mt-4" data-testid="product-price">${product.price}</div>
                    </div>

                    <div className="hairline" />

                    <p className="text-[#D6D6CC] leading-relaxed" data-testid="product-description">{localized.description}</p>

                    {/* Colours */}
                    {Array.isArray(product.colors) && product.colors.length > 0 && (
                        <div className="space-y-3" data-testid="color-picker">
                            <div className="flex items-center justify-between">
                                <span className="text-overline text-[#A3A3A3]">{t("product.select_color", { defaultValue: "Colour" })}</span>
                                <span className="text-overline text-[#A3A3A3]">{color || ""}</span>
                            </div>
                            <div className="flex flex-wrap gap-3">
                                {product.colors.map((c) => {
                                    const active = color === c.name;
                                    return (
                                        <button
                                            key={c.name}
                                            type="button"
                                            onClick={() => onColorPick(c)}
                                            data-testid={`color-${c.name}`}
                                            aria-label={c.name}
                                            title={c.name}
                                            className={`relative h-9 w-9 rounded-full border transition-all ${
                                                active ? "border-[#EDEDE6] ring-2 ring-[#EDEDE6]/30 ring-offset-2 ring-offset-[#0C0C0C]" : "border-white/15 hover:border-white/40"
                                            }`}
                                            style={{ background: c.swatch }}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Sizes */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-overline text-[#A3A3A3]">{t("product.select_size")}</span>
                            <button
                                type="button"
                                onClick={() => setSizeGuideOpen(true)}
                                data-testid="size-guide-trigger"
                                className="text-[11px] text-[#A3A3A3] hover:text-[#F4F4F0] underline-offset-2 hover:underline"
                            >
                                {t("product.size_guide")}
                            </button>
                        </div>
                        <div className="grid grid-cols-5 gap-2">
                            {product.sizes.map((s) => {
                                const stock = product.stock?.[s] ?? null;
                                const soldOut = stock === 0;
                                return (
                                    <button
                                        key={s}
                                        data-testid={`size-${s}`}
                                        onClick={() => !soldOut && setSize(s)}
                                        disabled={soldOut}
                                        className={`relative py-3 text-xs tracking-[0.18em] uppercase font-display transition-all border ${
                                            soldOut
                                                ? "border-white/5 text-[#444] line-through cursor-not-allowed"
                                                : size === s
                                                ? "bg-[#EDEDE6] text-[#0C0C0C] border-[#EDEDE6]"
                                                : "border-white/15 text-[#F4F4F0] hover:border-white/40"
                                        }`}
                                    >
                                        {s}
                                    </button>
                                );
                            })}
                        </div>
                        {selectedStock !== null && selectedStock > 0 && selectedStock <= 3 && (
                            <p data-testid="stock-low" className="text-[11px] text-[#591C1C] tracking-[0.18em] uppercase font-display mt-1">
                                {t("product.only_left_in", { count: selectedStock, size })}
                            </p>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3">
                        <button onClick={onAdd} className="btn-primary flex-1" data-testid="add-to-cart-button">
                            <ShoppingBag className="h-4 w-4" strokeWidth={1.5} /> {t("product.add_to_cart")}
                        </button>
                        <button
                            onClick={() => toggle(product)}
                            data-testid="pdp-wishlist-toggle"
                            aria-label={t("product.toggle_wishlist")}
                            className="px-5 border border-white/15 hover:border-white transition-colors"
                        >
                            <Heart className={`h-4 w-4 ${liked ? "fill-[#F4F4F0] text-[#F4F4F0]" : "text-[#F4F4F0]"}`} strokeWidth={1.4} />
                        </button>
                    </div>

                    {/* Details */}
                    <div className="hairline" />
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5 text-sm">
                        <div>
                            <dt className="text-overline text-[#666] mb-1.5">{t("product.fabric")}</dt>
                            <dd className="text-[#D6D6CC]">{product.fabric}</dd>
                        </div>
                        <div>
                            <dt className="text-overline text-[#666] mb-1.5">{t("product.origin")}</dt>
                            <dd className="text-[#D6D6CC]">{product.origin}</dd>
                        </div>
                    </dl>

                    <div className="hairline" />
                    <div className="grid grid-cols-3 gap-4 text-xs text-[#A3A3A3]">
                        <div className="space-y-1.5">
                            <Truck className="h-4 w-4 text-[#F4F4F0]" strokeWidth={1.3} />
                            <div className="text-overline text-[#F4F4F0]">{t("product.shipping_label")}</div>
                            <p>{t("product.shipping_body")}</p>
                        </div>
                        <div className="space-y-1.5">
                            <Recycle className="h-4 w-4 text-[#F4F4F0]" strokeWidth={1.3} />
                            <div className="text-overline text-[#F4F4F0]">{t("product.returns_label")}</div>
                            <p>{t("product.returns_body")}</p>
                        </div>
                        <div className="space-y-1.5">
                            <Wrench className="h-4 w-4 text-[#F4F4F0]" strokeWidth={1.3} />
                            <div className="text-overline text-[#F4F4F0]">{t("product.repair_label")}</div>
                            <p>{t("product.repair_body")}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Related */}
            {related.length > 0 && (
                <section className="container-rail py-20 border-t border-white/5">
                    <div className="flex items-end justify-between mb-12">
                        <div>
                            <div className="text-overline text-[#A3A3A3] mb-2">{t("product.related_label")}</div>
                            <h2 className="text-display text-3xl sm:text-4xl">{t("product.related_title")}</h2>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
                        {related.map((p, i) => (
                            <ProductCard key={p.id} product={p} index={i} />
                        ))}
                    </div>
                </section>
            )}

            <RecentlyViewed items={recent} excludeSlug={slug} />

            <Reviews slug={slug} />

            <SizeGuideDrawer
                open={sizeGuideOpen}
                onOpenChange={setSizeGuideOpen}
                category={product.category}
            />
        </div>
    );
}
